import { Text } from '@televet/kibble-ui/Text';
import { Flex } from '@televet/kibble-ui/chakra';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { PageContainer } from '@/components/PageContainer';
import { TablePagination } from '@/components/TablePagination';
import { OptOutsTable } from '@/features/workflows/components/OptOutsTable';
import { trpc } from '@/lib/trpc';
import { useListingFilters } from '@/features/listing-filters/useListingFilters';
import { useDebouncedFilterText } from '@/features/listing-filters/useDebouncedFilterText';
import {
  calendarDateToIsoEnd,
  calendarDateToIsoStart,
} from '@/features/listing-filters/calendarDate';
import { OptOutFilters } from './-components/OptOutFilters';
import {
  optOutsFiltersSchema,
  optOutsSearchSchema,
  type OptOutsFilterValues,
} from './-lib/filters';

const RouteComponent = () => {
  const navigate = Route.useNavigate();
  const { page = 1, ...filters } = Route.useSearch();

  const [targetDraft, setTargetDraft] = useDebouncedFilterText({
    value: filters.target,
    onCommit: (next) =>
      navigate({
        replace: true,
        search: (prev) => ({ ...prev, target: next, page: undefined }),
      }),
  });

  const applyFilters = useCallback(
    (next: OptOutsFilterValues) => {
      // Any filter change invalidates the current page number.
      navigate({ search: { ...next, page: undefined } });
    },
    [navigate]
  );

  useListingFilters({
    page: 'workflow-opt-outs',
    schema: optOutsFiltersSchema,
    filters,
    onRehydrate: applyFilters,
  });

  const { data: workflowsData } = useQuery(
    trpc.workflow.listWorkflows.queryOptions({ limit: 100 })
  );

  const { data, isLoading } = useQuery(
    trpc.workflow.listCommunicationOptOuts.queryOptions(
      useMemo(
        () => ({
          // The procedure takes a single workflow id, and the dropdown keeps
          // only one (see pickNewest in OptOutFilters), so this is the choice.
          ...(filters.workflowId?.length && { workflowId: filters.workflowId[0] }),
          ...(filters.scopeType?.length && { scopeType: filters.scopeType }),
          ...(filters.status?.length && { status: filters.status }),
          ...(filters.source?.length && { source: filters.source }),
          ...(filters.channel?.length && { channel: filters.channel }),
          createdFrom: calendarDateToIsoStart(filters.createdFrom),
          createdTo: calendarDateToIsoEnd(filters.createdTo),
          ...(filters.target && { target: filters.target }),
          page,
        }),
        [filters, page]
      )
    )
  );

  const goToPage = (nextPage: number) => {
    // Omit page 1 from the URL to keep the default view clean.
    navigate({
      search: (prev) => ({ ...prev, page: nextPage > 1 ? nextPage : undefined }),
    });
  };

  return (
    <PageContainer>
      <Flex direction="column" w="100%" gap={4}>
        <Breadcrumbs items={[{ label: 'Opt-outs' }]} />

        <Flex justify="space-between" align="center">
          <Text fontSize="lg" fontWeight="semibold" letterSpacing="-0.01em">
            Opt-outs
          </Text>
        </Flex>

        <OptOutFilters
          values={filters}
          onChange={applyFilters}
          workflows={workflowsData?.workflows ?? []}
          targetDraft={targetDraft}
          onTargetDraftChange={setTargetDraft}
        />
        {isLoading ? (
          <Flex justify="center" align="center" p={8}>
            <Text variant="subtle">Loading opt-outs…</Text>
          </Flex>
        ) : (
          <OptOutsTable optOuts={data?.optOuts ?? []} />
        )}
        {!isLoading && data?.optOuts.length === 0 && (
          <Flex justify="center" align="center" p={8}>
            <Text variant="subtle">No communication opt-outs found</Text>
          </Flex>
        )}
        {data?.pagination && (
          <TablePagination
            page={data.pagination.page}
            totalPages={data.pagination.totalPages}
            hasPrev={data.pagination.hasPrev}
            hasNext={data.pagination.hasNext}
            summary={`${data.optOuts.length} opt-outs on this page`}
            onPageChange={goToPage}
            data-testid-prefix="workflow-opt-outs-page"
          />
        )}
      </Flex>
    </PageContainer>
  );
};

export const Route = createFileRoute('/_authenticated/workflow-opt-outs/')({
  component: RouteComponent,
  validateSearch: optOutsSearchSchema,
});
